// Query-toolbar "Options" dropdown — folds the Safe Updates / Transaction /
// completion-sound toggles into one button to save toolbar space, plus a
// non-toggle "Sync schema" action. Same dropdown styling + toggle pattern as
// FormatMenu. Toggling keeps the menu open (flip several at once); the sync
// action closes it.
//
// Props:
//   safeMode                     string    — 'strict' | 'lax-key' | 'lax-any' | 'off'
//   onSafeMode(mode)             fn        — change handler
//   transaction / sound          bool      — current toggle states
//   onToggleTransaction / onToggleSound    fn
//   onSyncSchema                 fn        — refresh the autocomplete schema
//   schemaBusy                   bool      — disable + label the sync item while running
import { createSignal, Show } from 'solid-js';
import { useDismissable } from '../lib/dismissable.js';

// Order matters — this is also the cycle order when the user clicks the chip.
const SAFE_MODES = [
  { id: 'strict',  label: 'Strict',          desc: 'MySQL refuses UPDATE/DELETE without a key-based WHERE or LIMIT (composite PKs need the leftmost-prefix column).' },
  { id: 'lax-key', label: 'Lax — key column', desc: 'Ninocat refuses UPDATE/DELETE whose WHERE touches no INDEXED column (PK or secondary, no leftmost-prefix rule). LIMIT also passes.' },
  { id: 'lax-any', label: 'Lax — any WHERE',  desc: 'Ninocat refuses ONLY a bare UPDATE/DELETE with no WHERE at all. Any WHERE clause is accepted, indexed or not.' },
  { id: 'off',     label: 'Off',             desc: 'No safe-update guard. Be very careful — a bare DELETE FROM t will run.' },
];

export default function OptionsMenu(props) {
  const [open, setOpen] = createSignal(false);
  let btnRef, menuRef;
  useDismissable(() => [btnRef, menuRef], () => setOpen(false), { event: 'click' });
  const safeMode = () => props.safeMode || 'strict';

  return (
    <div class="export-menu">
      <button ref={btnRef} class="btn btn-sm btn-ghost" onClick={() => setOpen(!open())}
              title="Run options — Safe Updates mode, Transaction, completion sound, schema sync">
        ⚙ Options ▾
      </button>
      <Show when={open()}>
        <div ref={menuRef} class="export-menu-pop">
          <div class="export-menu-section-label">Safe Updates</div>
          {SAFE_MODES.map((m) => (
            <button class="export-menu-item export-menu-toggle"
                    onClick={() => props.onSafeMode?.(m.id)}
                    title={m.desc}>
              <span class="export-menu-check">{safeMode() === m.id ? '●' : ''}</span>{m.label}
            </button>
          ))}
          <div class="export-menu-sep" />
          <button class="export-menu-item export-menu-toggle" onClick={() => props.onToggleTransaction?.()}
                  title="Wrap runs in a transaction; auto-ROLLBACK on error; Commit / Rollback manually.">
            <span class="export-menu-check">{props.transaction ? '✓' : ''}</span>Transaction
          </button>
          <button class="export-menu-item export-menu-toggle" onClick={() => props.onToggleSound?.()}
                  title="Play a sound when a run finishes (success / error).">
            <span class="export-menu-check">{props.sound ? '✓' : ''}</span>Completion sound
          </button>
          <Show when={props.notifyAvailable}>
            <button class="export-menu-item export-menu-toggle" onClick={() => props.onToggleNotify?.()}
                    title="Send a notification to the configured target when a run finishes (success / failure).">
              <span class="export-menu-check">{props.notify ? '✓' : ''}</span>Completion notification
            </button>
          </Show>
          <button class="export-menu-item export-menu-toggle" onClick={() => props.onToggleAutosaveResults?.()}
                  title="Autosave each query result to Result History (a CSV snapshot you can reopen read-only). Large results are skipped.">
            <span class="export-menu-check">{props.autosaveResults ? '✓' : ''}</span>Autosave results
          </button>
          <div class="export-menu-sep" />
          <button class="export-menu-item" disabled={props.schemaBusy}
                  onClick={() => { setOpen(false); props.onSyncSchema?.(); }}
                  title="Refresh the autocomplete schema cache for this database — use it if the schema changed outside Ninocat.">
            {props.schemaBusy ? '⟳ Syncing schema…' : '⟳ Sync schema'}
          </button>
        </div>
      </Show>
    </div>
  );
}
